<template>
  <div>
    <div class="card">
      <div
        class="card-header"
        style="height: 60px;"
      >
        <h4 class="card-header-title">
          <template v-if="isFiltered">
            <strong>필터된 수업</strong> 참여 회원
          </template>
          <template v-else>
            <strong>오늘 전체</strong> 참여 회원
          </template>
        </h4>
      </div>

      <el-table
        v-if="data.length"
        v-loading="loading"
        :data="data"
        :max-height="620"
        v-el-table-infinite-scroll="detectBottom" 
        infinite-scroll-immediate
        height="100%"
        class="lesson-participate-table fifl-table"
        header-row-class-name="fifl-table-header-row"
        header-cell-class-name="fifl-table-header-cell"
        row-class-name="fifl-table-row"
        cell-class-name="fifl-table-cell"
        row-key="id"
        ref="participateTable"
        @row-click="handleRowClick"
        @filter-change="filterByBookingStatus"
      >
        <el-table-column
          prop="member"
          label="이름"
          fixed
          sortable
          :sort-method="columnSortByName"
          :width="tableColWidth.member"
        >
          <template slot-scope="scope">
            {{ scope.row.member.user.name }}{{ scope.row.enrollment_is_removed ? ' ' + '(삭제됨)' : '' }}
          </template>
        </el-table-column>
        <el-table-column
          label="상태"
          prop="status"
          :filtered-value="selectedStatus"
          :filters="filteredStatusFilter"
          :width="tableColWidth.status"
        >
          <template slot-scope="scope">
            <el-status-tag :label="getBookingStatusInLabel(scope.row)" />
          </template>
        </el-table-column>
        <el-table-column
          prop="member.gender"
          label="성별"
          sortable
          :width="tableColWidth.gender"
        >
          <template slot-scope="scope">
            {{ scope.row.member.gender % 2 === 1 ? '남성' : '여성' }}
          </template>
        </el-table-column>
        <el-table-column
          label="연령"
          sortable
          prop="member.age"
          :width="tableColWidth.age"
        >
          <template slot-scope="scope">
            {{ scope.row.member.age }}세
          </template>
        </el-table-column>
        <el-table-column
          prop="child_membership.end"
          sortable
          :width="tableColWidth.membership_end"
        >
          <template 
            slot="header" 
          > 
            사용 회원권 종료일
            <popover
              placement="bottom-start"
            >   
              회원이 본 수업을 예약할 때 사용한 회원권의<br>종료일입니다. 다른 유효 회원권이 있을 경우<br>예상하신 종료일이 다를 수 있습니다.    
            </popover>
          </template>
          <template slot-scope="scope">
            {{ format(scope.row.child_membership.end, 'YY.MM.DD') }}
          </template>
        </el-table-column>
        <el-table-column
          label="최근 방문일"
          prop="member.last_visit"
          sortable
          :width="tableColWidth.last_visit"
        >
          <template slot-scope="scope">
            {{ scope.row.member.last_visit ? format(scope.row.member.last_visit, 'YY.MM.DD') : '' }}
          </template>
        </el-table-column>
        <el-table-column
          width="50"
          class-name="more-action__column"
          fixed="right"
        >
          <template slot-scope="scope">
            <attendance-control-dropdown
              :key="scope.row.id"
              :data="scope.row"
              :disabled="getBookingStatusInLabel(scope.row) === PRESENT_BOOKING_STATUS.WAITING"
              @update="onAttendanceUpdate"
            />
          </template>
        </el-table-column>
        <template 
          v-if="isSpinnerActive"
          slot="append"
        >
          <section
            class="spinner-container"
            @wheel="setSpinnerCenter"
          >
            <div>
              <el-spinner :stroke-width="1" />
            </div>
          </section>
        </template>
      </el-table>
      <empty-table 
        v-else
        :props-min-width="'initial'"
        :props-loading-status="loading"
        :props-has-parent="emptyTableProps.hasParent"
        :props-is-icon="emptyTableProps.isIcon"
        :props-header-height="emptyTableProps.headerHeight"
        :props-data="emptyTableProps.data"
        :props-table-body-height="emptyTableProps.bodyHeight"
        :props-message="emptyTableProps.message" 
        :props-initialzed="emptyTableProps.initialized" 
        :props-is-filtered="selectedStatus ? selectedStatus.length > 0 : false" 
      />
    </div>
  </div>
</template>

<script>
import EmptyTable from '@/components/EmptyTable';
import ElStatusTag from '../../components/element-ui/ElStatusTag';
import Popover from '@/components/popover';
import dateFns from 'date-fns';
import AttendanceControlDropdown from '../../components/AttendanceControlDropdown';
import {
  getBookingStatus,
  PRESENT_BOOKING_STATUS
} from '../../utils/booking-status';
import elTableInfiniteScroll from 'el-table-infinite-scroll';

const RESPONSIVE_TABLE_WIDTH = {
  mobile: {
    member: 70,
    status: 70,
    gender: 70,
    age: 70,
    membership_end: 150,
    last_visit: 80
  },
  desktop: {
    member: 125,
    status: null,
    gender: 80,
    age: 70,
    membership_end: 160,
    last_visit: 110
  }
};

export default {
  name: 'LessonParticipateTable',
  components: {
    AttendanceControlDropdown,
    ElStatusTag,
    EmptyTable,
    Popover
  },
  directives: {
    'el-table-infinite-scroll': elTableInfiniteScroll
  },
  props: [
    'data',
    'isFiltered',
    'loading',
    'total',
    'isAllDataFetch',
    'selectedStatus'
  ],
  data() {
    return {
      PRESENT_BOOKING_STATUS,
      statusFilters: [
        {
          text: '예약',
          visibleInBookingSystem: true,
          visibleInNonBookingSystem: false,
          value: 'BOOKED'
        },
        {
          text: '대기',
          visibleInBookingSystem: true,
          visibleInNonBookingSystem: false,
          value: 'WAITING'
        },
        {
          text: '출석',
          visibleInBookingSystem: true,
          visibleInNonBookingSystem: true,
          value: 'ATTENDANCE'
        },
        {
          text: '지각',
          visibleInBookingSystem: true,
          visibleInNonBookingSystem: true,
          value: 'LATE'
        },
        {
          text: '결석',
          visibleInBookingSystem: true,
          visibleInNonBookingSystem: true,
          value: 'ABSENCE'
        }
      ],
      emptyTableProps: {
        message: '검색결과가 없습니다.',
        initialized: {
          message: '필터 초기화하기',
          handler: e => {
            e.preventDefault();
            this.$emit('filter-change', []);
          }
        },
        bodyHeight: 578,
        isIcon: false,
        hasParent: true,
        headerHeight: 51,
        data: [
          { label: '이름', width: 125 + 'px' },
          { label: '상태', width: 100 + 'px' },
          { label: '성별', width: 80 + 'px' },
          { label: '연령', width: 70 + 'px' },
          { label: '사용 회원권 종료일', width: 160 + 'px' },
          { label: '최근 방문일', width: 110 + 'px' },
          { label: '', width: 50 + 'px' }
        ]
      },
      isSpinnerActive: true
    };
  },
  computed: {
    tableColWidth() {
      let isMobileWidth = window.innerWidth < 768;

      return RESPONSIVE_TABLE_WIDTH[isMobileWidth ? 'mobile' : 'desktop'];
    },
    isBookingSystem() {
      return this.$store.getters.getIsBookingSystem;
    },
    filteredStatusFilter() {
      return this.statusFilters.filter(statusFilter => {
        if (this.isBookingSystem) {
          return statusFilter.visibleInBookingSystem;
        }

        return statusFilter.visibleInNonBookingSystem;
      });
    }
  },
  watch: {
    isAllDataFetch(result) {
      if (result) {
        this.isSpinnerActive = false;
      } else {
        this.isSpinnerActive = true;
      }
    }
  },
  methods: {
    format: dateFns.format,
    onAttendanceUpdate() {
      this.$emit('update', {});
    },
    handleRowClick(row, column, event) {
      if (!column.label) {
        return;
      }

      let isNewTab = event.shiftKey;
      let memberId = row.child_membership.enrollment;

      if (isNewTab) {
        let routeData = this.$router.resolve({ path: `/member/${memberId}` });
        return window.open(routeData.href, '_blank');
      }

      this.$router.push(`/member/${memberId}`).catch(() => {});
    },
    filterByBookingStatus(col) {
      const [filters] = Object.values(col);
      this.$emit('filter-change', filters);
    },
    getBookingStatusInLabel(row) {
      const bookingStatus = getBookingStatus(row);

      return PRESENT_BOOKING_STATUS[bookingStatus];
    },
    // set prefix by Priority
    // 숫자 > 기호 > 한글자모 > 한글단어 > 영어소문자 > 영어대문자
    setOrderPrefix(setPrefixTarget) {
      let prefix;
      // 숫자
      if (typeof setPrefixTarget === 'number') {
        prefix = '1';
      } else {
        //change first String to UTF-16 code
        const utfCode = setPrefixTarget.charCodeAt(0);
        // 한글 자모 3130—318F
        if (0x3130 <= utfCode && utfCode <= 0x318f) prefix = '3';
        // 한글 AC00—D7AF
        else if (0xac00 <= utfCode && utfCode <= 0xd7af) prefix = '4';
        // 영어 소문자 0061-007A
        else if (0x61 <= utfCode && utfCode <= 0x7a) prefix = '5';
        // 영어 대문자 0041-007A
        else if (0x41 <= utfCode && utfCode <= 0x5a) prefix = '6';
        // 그외 기호
        else prefix = '2';
      }
      return prefix + setPrefixTarget;
    },
    //name sort method
    columnSortByName(prev, next) {
      const compareA = this.setOrderPrefix(prev.member.user.name);
      const compareB = this.setOrderPrefix(next.member.user.name);
      if (compareA < compareB) {
        return -1;
      }
      if (compareA > compareB) {
        return 1;
      }
      return 0;
    },
    detectBottom() {
      this.$emit('scroll');
    },
    setSpinnerCenter(e) {
      const tableBody = e.target.parentElement.parentElement.parentElement;
      const targetParent = e.target.parentElement;

      targetParent.style.transform = `translateX(${tableBody.scrollLeft +
        'px'})`;
    }
  }
};
</script>
<style lang="scss" scoped>
.lesson-participate-table {
  width: 100%;
  height: 625px;
}

.fifl-table {
  margin-bottom: 10px;
}

.spinner-container {
  position: relative;
  width: 100%;
  height: 50px;
  text-align: center;
  overflow: hidden;

  div {
    position: relative;
    width: 100%;
    height: 50px;
  }
}
</style>
